import confetti from 'canvas-confetti';

// Rich, celebratory palette — gold core + vibrant accents for a "more colorful" burst.
const GOLD = ['#c6a15b', '#e7d3a1', '#f7f3ec', '#a8823c', '#d8b96a', '#b8860b'];
const VIBRANT = ['#c6a15b', '#e7d3a1', '#ff5e9c', '#ff9f1c', '#8a3ffc', '#25d0c0', '#ff3b6b', '#f7f3ec'];

// Trim particle density on small screens so phones stay snappy; full richness on desktop.
const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 640;
const n = (count) => Math.round(count * (isMobile() ? 0.4 : 1));

// Soft gold "chime" — a gentle two-note bell synthesized with Web Audio (no file needed).
let chimeCtx = null;
const playChime = () => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!chimeCtx) chimeCtx = new AC();
    if (chimeCtx.state === 'suspended') chimeCtx.resume().catch(() => {});
    const t0 = chimeCtx.currentTime;
    const master = chimeCtx.createGain();
    master.gain.value = 0.14;
    master.connect(chimeCtx.destination);
    // Bell partials: E6 strike, B6 shimmer a heartbeat later, faint high sparkle.
    const notes = [
      { f: 1318.51, at: 0, peak: 0.55, dur: 1.1 },
      { f: 1975.53, at: 0.09, peak: 0.32, dur: 1.0 },
      { f: 2637.02, at: 0.09, peak: 0.1, dur: 0.8 },
    ];
    notes.forEach(({ f, at, peak, dur }) => {
      const osc = chimeCtx.createOscillator();
      const g = chimeCtx.createGain();
      osc.type = 'sine';
      osc.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t0 + at);
      g.gain.exponentialRampToValueAtTime(peak, t0 + at + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + dur);
      osc.connect(g);
      g.connect(master);
      osc.start(t0 + at);
      osc.stop(t0 + at + dur + 0.05);
    });
  } catch (e) { if (process.env.NODE_ENV !== 'production') console.debug('chime unavailable', e); }
};

// Custom brand-mark confetti: the gold "B" monogram, drawn as a bitmap confetti shape
// (same mechanism canvas-confetti uses for shapeFromText). Loaded once and cached.
let brandShapePromise = null;
const getBrandShape = () => {
  if (brandShapePromise) return brandShapePromise;
  if (typeof window === 'undefined' || typeof createImageBitmap !== 'function') {
    brandShapePromise = Promise.resolve(null);
    return brandShapePromise;
  }
  brandShapePromise = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = async () => {
      try {
        const bitmap = await createImageBitmap(img);
        const scalar = 2.4; // brand marks read best a touch larger than default confetti
        const scale = 1 / scalar;
        resolve({
          type: 'bitmap',
          bitmap,
          matrix: [scale, 0, 0, scale, (-bitmap.width * scale) / 2, (-bitmap.height * scale) / 2],
        });
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = '/confetti-mark.png';
  });
  return brandShapePromise;
};

// Fire a small extra burst of the gold "B" monogram mixed into a celebration.
const fireBrandMarks = (origin, { count = 26, spread = 190, startVelocity = 48, gravity = 1, ticks = 200 } = {}) => {
  getBrandShape().then((shape) => {
    if (!shape) return;
    confetti({
      particleCount: n(count),
      spread,
      startVelocity,
      origin,
      shapes: [shape],
      scalar: 2.4,
      ticks,
      gravity,
      drift: 0,
      zIndex: 10000,
      flat: true,
      disableForReducedMotion: true,
    });
  });
};

export const fireConfetti = () => {
  // 1. Huge central explosion (+300%, wider spread)
  confetti({ particleCount: n(7904), spread: 218, startVelocity: 64, origin: { y: 0.66 }, colors: VIBRANT, ticks: 360, scalar: 1.15, zIndex: 10000 });
  // 2. Dual side cannons (+300%, wider spread)
  confetti({ particleCount: n(3952), angle: 60, spread: 143, startVelocity: 66, origin: { x: 0, y: 0.88 }, colors: VIBRANT, ticks: 360, zIndex: 10000 });
  confetti({ particleCount: n(3952), angle: 120, spread: 143, startVelocity: 66, origin: { x: 1, y: 0.88 }, colors: VIBRANT, ticks: 360, zIndex: 10000 });

  // 3. Golden shimmer raining from the top (delayed + long-lived)
  setTimeout(() => {
    confetti({ particleCount: n(6080), spread: 278, startVelocity: 34, origin: { y: -0.1 }, colors: GOLD, ticks: 420, scalar: 1.0, gravity: 0.75, zIndex: 10000 });
  }, 180);

  // 4. Sparkly star finish
  setTimeout(() => {
    confetti({ particleCount: n(3648), spread: 203, startVelocity: 48, origin: { y: 0.6 }, colors: VIBRANT, shapes: ['star'], scalar: 1.4, ticks: 340, zIndex: 10000 });
  }, 340);

  // 5. Final lingering drift so it lasts longer
  setTimeout(() => {
    confetti({ particleCount: n(2736), spread: 248, startVelocity: 24, origin: { y: 0.2 }, colors: GOLD, ticks: 380, scalar: 0.9, gravity: 0.6, zIndex: 10000 });
  }, 560);

  // 6. Signature "B" monogram marks tumbling through the gold — spread wide
  fireBrandMarks({ y: 0.62 }, { count: 60, spread: 300, startVelocity: 58, ticks: 320, gravity: 0.9 });
  setTimeout(() => fireBrandMarks({ y: -0.05 }, { count: 40, spread: 340, startVelocity: 36, ticks: 360, gravity: 0.7 }), 220);
};

// Quick, refined gold burst for add-to-bag moments — spread wide across the screen. Originates near (x, y) in [0,1] viewport coords.
// zIndex sits above every modal/drawer (QuickView is z-130) so the burst is always visible.
export const fireCartConfetti = (x = 0.5, y = 0.5) => {
  playChime();
  confetti({ particleCount: n(1680), spread: 210, startVelocity: 48, origin: { x, y }, colors: GOLD, ticks: 150, scalar: 0.85, gravity: 1.15, zIndex: 10000, disableForReducedMotion: true });
  confetti({ particleCount: n(608), spread: 250, startVelocity: 38, origin: { x, y }, colors: GOLD, shapes: ['star'], scalar: 1.1, ticks: 170, gravity: 1, zIndex: 10000, disableForReducedMotion: true });
  // Signature gold "B" monogram mixed right into the add-to-bag burst — wide arc
  fireBrandMarks({ x, y }, { count: 24, spread: 230, startVelocity: 46, ticks: 190, gravity: 1.05 });
};
